import { AnyTranslationsFn, Locale, LocaleMessageType } from "@/types/locale";
import { useCallback } from "react";
import { useTranslations } from "next-intl";

type UntypedUseTranslations = (namespace?: string) => AnyTranslationsFn;

const useUntypedTranslations = useTranslations as unknown as UntypedUseTranslations;

const isPlainRecord = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const isNullableString = (value: unknown): value is string | null | undefined =>
  value === null || value === undefined || typeof value === "string";

/**
 * Checks if the provided object is an instance of a localized string.
 *
 * A localized string is an object that contains message keys for specific languages,
 * such as 'ru' for Russian and 'en' for English.
 *
 * @param {unknown} object - The object to be checked.
 * @returns {boolean} Returns true if the object represents a localized string; otherwise, false.
 */
export const isInstanceOfLocalizeString = (object: unknown): object is LocaleMessageType => {
  if (!isPlainRecord(object)) {
    return false;
  }

  // Both locale keys are optional, so the API's empty localization object is valid too.
  return Object.entries(object).every(
    ([key, value]) => (key === "ru" || key === "en") && isNullableString(value),
  );
};

/**
 * Returns a localized string based on the specified locale and given localization object.
 * If the locale is not found in the localization object, a default value is returned if provided.
 *
 * @param {unknown} from - A plain string or an object containing localized messages for different locales.
 * @param {Locale | string} locale - The target locale for localization. Can either be a Locale object or a string.
 *                                    Special cases: "RUS" is mapped to "ru" and "ENG" is mapped to "en".
 * @param {string} [defaultValue] - The default string to return if the specified locale is not found in the localization object.
 * @returns {string} The localized string corresponding to the specified locale or the default value if not found.
 */
export const localizeFrom = (
  from: unknown,
  locale: Locale | string,
  defaultValue?: string,
): string => {
  const fallback = defaultValue ?? "";

  if (typeof from === "string") {
    return from || fallback;
  }

  if (!isPlainRecord(from)) {
    return fallback;
  }

  const correctLocale = locale === "RUS" ? "ru" : locale === "ENG" ? "en" : locale;
  const localizedValue = from[correctLocale];

  return typeof localizedValue === "string" && localizedValue ? localizedValue : fallback;
};

/**
 * A utility function that wraps the usage of translations from a specified namespace.
 * This function provides a translation function `t` to retrieve translations for any given key.
 *
 * @param {string} [namespace] - The optional namespace to scope the translations. If not provided, translations are not namespace-scoped.
 * @returns {Function} A function that takes a translation key (`anyKey`) as a string and returns the corresponding translated value.
 */
export const useAnyTranslations = (namespace?: string): AnyTranslationsFn => {
  const t = useUntypedTranslations(namespace);

  return useCallback((anyKey: string, options?: object) => t(anyKey, options), [t]);
};

export { localizeFrom as localeFrom };
